import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Check, ArrowLeft, ArrowRight, PlayCircle, LayoutGrid, FileText } from "lucide-react";
import { toast } from "sonner";

export const TYPE_LABEL = { episode: "Episode", carousel: "Carousel", guide: "Guide" };
export const TYPE_ICON = { episode: PlayCircle, carousel: LayoutGrid, guide: FileText };
export const HANDLE = "@bapnadigital";

export function fmtIST(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

export function epCode(n) {
  return n == null ? "" : `EP ${String(n).padStart(2, "0")}`;
}

export function Markdown({ children, testid }) {
  return <div className="learn-md" data-testid={testid}><ReactMarkdown remarkPlugins={[remarkGfm]}>{children || ""}</ReactMarkdown></div>;
}

export function LearnCard({ item, testid }) {
  const Icon = TYPE_ICON[item.type];
  return (
    <Link to={item.path} data-testid={testid || `learn-card-${item.external_id}`}
      className="group flex h-full flex-col overflow-hidden rounded-[10px] border border-border bg-card transition-colors hover:border-accent/60">
      <div className="aspect-[4/5] w-full overflow-hidden border-b border-border bg-[#F1EDE4]">
        {item.cover_url
          ? <img src={item.cover_url} alt={item.title} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]" />
          : <div className="flex h-full items-center justify-center"><Icon className="h-10 w-10 text-muted" strokeWidth={1.5} /></div>}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <p className="mb-1.5 flex flex-wrap items-center gap-x-2 text-[11px] font-semibold uppercase tracking-wider text-muted">
          <span className="text-accent">{item.type === "episode" && item.ep != null ? epCode(item.ep) : TYPE_LABEL[item.type]}</span>
          {item.topic && <span>· {item.topic}</span>}
        </p>
        <h3 className="font-heading text-base font-bold leading-snug text-ink group-hover:text-accent">{item.title}</h3>
        {item.summary && <p className="mt-1.5 line-clamp-2 text-sm text-muted">{item.summary}</p>}
        <p className="mt-auto pt-3 text-xs text-muted">{fmtIST(item.publish_at)}</p>
      </div>
    </Link>
  );
}

export function PromptBox({ prompt }) {
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  if (!prompt) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(prompt); } catch {
      const r = document.createRange(); r.selectNodeContents(ref.current); const s = window.getSelection(); s.removeAllRanges(); s.addRange(r); document.execCommand("copy");
    }
    setDone(true); toast.success("Prompt copied"); setTimeout(() => setDone(false), 2000);
  };
  return (
    <section className="rounded-[10px] border border-accent/40 bg-[#FBF1EC] p-5" data-testid="learn-prompt-box">
      <div className="flex items-center justify-between gap-3">
        <p className="kicker text-xs text-accent">Copy-paste AI prompt</p>
        <button onClick={copy} data-testid="learn-prompt-copy"
          className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90">
          {done ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{done ? "Copied" : "Copy"}
        </button>
      </div>
      <pre ref={ref} className="mt-3 whitespace-pre-wrap break-words font-mono text-[13.5px] leading-relaxed text-ink" data-testid="learn-prompt-text">{prompt}</pre>
    </section>
  );
}

export function Sources({ sources }) {
  if (!sources?.length) return null;
  return (
    <section data-testid="learn-sources">
      <h2 className="font-heading text-lg font-bold text-ink">Sources</h2>
      <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm">
        {sources.map((s, i) => (
          <li key={i}>{s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-accent underline-offset-2 hover:underline">{s.title || s.url}</a> : s.title}</li>
        ))}
      </ol>
    </section>
  );
}

export function PrevNext({ previous, next, prefix, label = "" }) {
  if (!previous && !next) return null;
  const name = (x) => (x.ep != null ? `${epCode(x.ep)} · ${x.title}` : x.title);
  return (
    <nav className="grid gap-3 border-t border-border pt-6 sm:grid-cols-2" data-testid="learn-prevnext">
      {previous ? (
        <Link to={`/learn/${prefix}/${previous.slug}`} className="group rounded-[10px] border border-border bg-card p-4" data-testid="learn-prev">
          <span className="kicker inline-flex items-center gap-1 text-[11px] text-accent"><ArrowLeft className="h-3 w-3" /> Previous {label}</span>
          <span className="mt-1 block font-heading font-semibold text-ink group-hover:text-accent">{name(previous)}</span>
        </Link>
      ) : <span />}
      {next && (
        <Link to={`/learn/${prefix}/${next.slug}`} className="group rounded-[10px] border border-border bg-card p-4 sm:text-right" data-testid="learn-next">
          <span className="kicker inline-flex items-center gap-1 text-[11px] text-accent">Next {label} <ArrowRight className="h-3 w-3" /></span>
          <span className="mt-1 block font-heading font-semibold text-ink group-hover:text-accent">{name(next)}</span>
        </Link>
      )}
    </nav>
  );
}

export function LearnHeader({ item, kicker }) {
  return (
    <header>
      <Link to="/learn" className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-muted hover:text-ink" data-testid="learn-back"><ArrowLeft className="h-4 w-4" /> Learn with {HANDLE}</Link>
      <p className="kicker text-xs text-accent" data-testid="learn-kicker">{kicker}<span className="text-muted"> · {fmtIST(item.publish_at)}{item.topic ? ` · ${item.topic}` : ""}</span></p>
      <h1 className="mt-2 font-heading text-[clamp(1.75rem,5vw,2.6rem)] font-extrabold leading-tight text-ink" data-testid="learn-title">{item.title}</h1>
      {item.summary && <p className="mt-3 text-lg text-muted">{item.summary}</p>}
    </header>
  );
}

export const absUrl = (p) => (p ? (p.startsWith("http") ? p : `${window.location.origin}${p}`) : undefined);
